import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment';
import { StorageService } from '@core/services/util/storage.service';
import { BrandPass } from '@shared/models/brand-pass';
import { LoginService } from '@core/services/login/login.service';
import { SessionStorageService } from '../util/session-storage.service';
import { ApplicableOfferDetailsAPIResponse } from '@app/shared/models/api-response/pass-api-response';

export enum MEMBERSHIP_ACTION {
    EXPLORE_BRANDS = 'Explore Brands',
    DOWNLOAD_EATCLUB = 'Download EatClub',
    START_ORDERING = 'Start Ordering',
    RENEW_MEMBERSHIP = 'Renew Membership',
    APPLY_EATCLUB_AND_PROCEED = 'Apply EatClub and Proceed'
}

@Injectable({
    providedIn: 'root'
})
export class MultiBrandPassService {
    constructor(
        private storageService: StorageService,
        private http: HttpClient,
        private loginService: LoginService,
        private sessionStorage: SessionStorageService
    ) {}

    getPassInfo(): Promise<ApplicableOfferDetailsAPIResponse> {
        return new Promise(async (resolve, reject) => {
            const sessionId = await this.loginService.ensureSessionId(true);
            const passes = this.sessionStorage.get('passes');
            if (passes) {
                return resolve(passes);
            }

            try {
                const outlet_id = this.storageService.get('outlet_id') ? this.storageService.get('outlet_id') : 1;
                const response: any = await this.http
                    .get<ApplicableOfferDetailsAPIResponse>(
                        `${environment.c3pServiceUrl}/offer_details/applicable_offer_details`,
                        {
                            params: {
                                app_id: environment.brand.id,
                                outlet_id: outlet_id
                            },
                            headers: {
                                Authorization: `Bearer ${sessionId}`
                            }
                        }
                    )
                    .toPromise();

                if (response.meta.code === 200) {
                    this.sessionStorage.set('passes', response);
                    resolve(response);
                } else {
                    return reject(response);
                }
            } catch (error) {
                return reject(error);
            }
        });
    }

    createPassData(passes: any): Array<object> {
        const pass_products = [];
        passes?.forEach((pass: any) => {
            const brand_pass = new BrandPass(pass);
            pass_products.push(brand_pass);
        });
        return pass_products;
    }

    getSubItemPresentInCart(passes: any, items: any) {
        if (!items || !Object.keys(items).length) {
            return null;
        }
        for (const item of Object.values(items) as any) {
            if (item.is_subscription_item) {
                return passes.filter(pass => item.ids.product_id === pass.product_id)[0];
            }
        }
        return null;
    }

    getDfltPass(passes: Array<object>, trial_expired: boolean): any {
        if (!passes.length) {
            return undefined;
        }

        if (trial_expired) {
            passes = passes.filter((pass: any) => !pass.trial);
        }

        if (passes.length) {
            const dfltPass = passes.find((pass: any) => Boolean(pass.dflt));
            return dfltPass ? dfltPass : passes[0];
        }
    }

    getButtonText(stage: string): string {
        switch (stage) {
            case 'active':
                return MEMBERSHIP_ACTION.START_ORDERING;
            case 'renew':
                return MEMBERSHIP_ACTION.RENEW_MEMBERSHIP;
            default:
                return MEMBERSHIP_ACTION.APPLY_EATCLUB_AND_PROCEED;
        }
    }

    getCTAText(isDefaultOutlet: boolean, stage: string, isTrialPass: boolean): string {
        if (isDefaultOutlet) {
            return MEMBERSHIP_ACTION.EXPLORE_BRANDS;
        } else if (isTrialPass && environment.brand.id !== 19) {
            return MEMBERSHIP_ACTION.DOWNLOAD_EATCLUB;
        } else {
            return this.getButtonText(stage);
        }
    }

    getCustomerPassStage(stage: string) {
        switch (stage) {
            case 'active':
                return 'active';

            case 'near-expiry':
            case 'expired':
                return 'renew';

            default:
                return 'inactive';
        }
    }

    getBrandIcons(participating_brands) {
        if (!participating_brands) {
            return [];
        }

        return participating_brands.map(id => `${environment.assetsUrl}/icon/web/brand/${id}`);
    }

    getBestValuePassId(passes, eatclub_trial_expired) {
        const bestValuePass = {};
        for (const pass of passes) {
            if (pass.trial && eatclub_trial_expired) {
                continue;
            }

            const pricePerMonth = pass.discounted_price / (pass.validity / 30);
            // to get a pass which has the lowest price per month, if two passs have the same price per month, then we choose the one with less validity
            if (
                !('pricePerMonth' in bestValuePass) ||
                pricePerMonth < Number(bestValuePass['pricePerMonth']) ||
                (bestValuePass['pricePerMonth'] == pricePerMonth && bestValuePass['bestPassExpiry'] > pass.validity)
            ) {
                bestValuePass['bestPassId'] = pass.id;
                bestValuePass['bestPassExpiry'] = pass.validity;
                bestValuePass['pricePerMonth'] = pricePerMonth;
            }
        }
        return bestValuePass;
    }
}
